"use client"
import { memo } from "react"
import type { ComparisonBranch } from "./MetricScenarioChart"
import type { BranchKey, ComparisonPlanId, MetricAxis, MetricScenarioSeries, SeriesPoint } from "./metricDetailsTypes"

// Plan A is the mono-black protagonist; the comparison plans take whatever
// color the caller assigned their line (Plan D red, B/C/S black in the
// production panels; all red in the legacy content), so the bars match the
// chart they replace. B/C/S de-emphasize as HOLLOW bars — hairline black
// outline, no fill — never gray (2026-07-09); zero-total plans read "none"
// in italic black.
const PLAN_A_INK = "#111111"
const COMPARISON_FALLBACK_INK = "var(--race-background)"

// The chart's title. Doubles as the redesign panel's trajectory-zone section
// title (metricPanelRedesigns), so the two can never disagree.
export const SLOWDOWN_TOTAL_TITLE = "Total slowdown incurred before superintelligence"

// Plan id → display name, keyed so the render site's lookup lands on the
// literal (cmd-click). "A" plus the comparison plan ids (D, B, C, S).
const PLAN_NAME: Record<"A" | ComparisonPlanId, string> = {
  A: "Plan A",
  S: "Plan S",
  B: "Plan B",
  C: "Plan C",
  D: "Plan D",
}

// Plan S holds capability flat — it never hands off to superintelligence, so
// its slowdown isn't a finite "total before superintelligence" the way the
// other plans' are; the series' 2040 snapshot (~11 yr) undersells an
// open-ended pause. Show it as an open ">15 yr" bar instead (value drives the
// bar length; label is printed verbatim).
const OPEN_ENDED_TOTAL: Partial<Record<"A" | ComparisonPlanId, { value: number; label: string }>> = {
  S: { value: 15, label: "15+ yr" },
}

// Matches the line chart's "years" unit formatting: sub-year totals keep one
// decimal (0.3 yr), whole years round.
const fmtYears = (v: number) => (v > 0 && v < 1 ? v.toFixed(1) : String(Math.round(v))) + " yr"

const lastValue = (pts: SeriesPoint[]) => (pts.length > 0 ? pts[pts.length - 1].value : 0)

type MetricBarChartProps = {
  // Plan A's trunk; its last branch-A point is Plan A's total.
  series: MetricScenarioSeries
  // The other plans (D, B, C, S) with their per-plan series and line color.
  comparisonBranches: ComparisonBranch[]
  axis: MetricAxis
  // Plan A's authored end label (e.g. "~9 yr"); other plans derive from data.
  endLabels?: Partial<Record<BranchKey, string>>
  // Shows the SLOWDOWN_TOTAL_TITLE caption above the bars. Off where a host
  // already prints that title as its own section header (the redesign panels).
  showCaption?: boolean
}

// A compact horizontal bar chart for a *cumulative* metric: one bar per plan
// showing the total accrued by the end of its scenario (the last point of each
// series), rather than the trajectory over time. Drawn in the HUD's own idiom
// — mono uppercase labels, hairline scale, page background, no enclosing box —
// so it reads as one more state of the same instrument.
export const MetricBarChart = memo(({ series, comparisonBranches, axis, endLabels, showCaption = true }: MetricBarChartProps) => {
  const entries = [
    { id: "A" as const, value: lastValue(series.branches.A), ink: PLAN_A_INK, endLabel: endLabels?.A },
    ...comparisonBranches.map((b) => {
      const override = OPEN_ENDED_TOTAL[b.id]
      return {
        id: b.id,
        value: override?.value ?? lastValue(b.points),
        ink: b.color ?? COMPARISON_FALLBACK_INK,
        endLabel: override?.label,
      }
    }),
  ]

  // Domain top: round the largest total up to a whole year so the fullest bar
  // has a clean scale end (the authored axis max, 10 yr, is too low for Plan S).
  const domainMax = Math.max(1, Math.ceil(Math.max(...entries.map((e) => e.value))))

  // Biggest bar on top; a 0-total plan reads "none" beside an empty track.
  // The B/C/S context plans draw hollow; A and D keep solid fills.
  const bars = entries
    .sort((a, b) => b.value - a.value)
    .map(({ id, value, ink, endLabel }) => ({
      id,
      name: PLAN_NAME[id],
      ink,
      hollow: id !== "A" && id !== "D",
      isZero: value === 0,
      label: value === 0 ? "none" : endLabel ?? fmtYears(value),
      pct: Math.min(100, (value / domainMax) * 100),
    }))

  return (
    <div className="font-mono">
      {showCaption && (
        <div className="mb-4 text-[10px] uppercase tracking-wider text-[#111111]">
          {SLOWDOWN_TOTAL_TITLE}
        </div>
      )}

      <div className="flex flex-col gap-3">
        {bars.map(({ id, name, ink, label, pct, isZero, hollow }) => (
          <div key={id}>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: ink }}>{name}</span>
              <span
                className={isZero ? "text-[13px] italic leading-none" : "text-[15px] font-bold leading-none tabular-nums"}
                style={{ color: ink }}
              >
                {label}
              </span>
            </div>
            <div className="h-5 w-full bg-black/[0.06]">
              <div
                className="h-full transition-[width] duration-700 ease-out"
                style={{
                  width: `${pct}%`,
                  ...(hollow
                    ? { border: "1px solid #111111", background: "#fffff8" }
                    : { backgroundColor: ink }),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Shared 0 → max scale under the aligned tracks. */}
      <div className="mt-1.5 flex justify-between text-[8.5px] text-[#111111]">
        <span>0</span>
        <span>{domainMax} yr</span>
      </div>
    </div>
  )
})
MetricBarChart.displayName = "MetricBarChart"
